import React from 'react';
import { usePower } from '../state/PowerContext';

function fmtValue(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function fmtLmp(lmp) {
  if (lmp == null) return '—';
  const sign = lmp < 0 ? '-' : '';
  return `${sign}$${Math.abs(lmp).toFixed(2)}`;
}

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}:00 ${ampm}`;
}

function fmtFullDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
}

function prevDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() - 1);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function nextDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// Click-to-expand details for the period peak: full date, surrounding hours
// (3 before + 3 after), value/temp/$LMP for each, source (forecast vs actual).
export default function PeakDetailsModal({ peak, records, onClose }) {
  const { series } = usePower();
  if (!peak) return null;

  const byKey = new Map((records || []).map(r => [`${r.date}_${r.hour}`, r]));

  // Build 7 cells: peak-3, peak-2, peak-1, peak, peak+1, peak+2, peak+3
  const cells = [];
  for (let offset = -3; offset <= 3; offset++) {
    let date = peak.date;
    let hour = peak.hour + offset;
    while (hour < 1) { hour += 24; date = prevDate(date); }
    while (hour > 24) { hour -= 24; date = nextDate(date); }
    cells.push({
      date, hour,
      isPeak: offset === 0,
      offset,
      rec: byKey.get(`${date}_${hour}`) || null,
    });
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 720 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
          <h2 style={{ margin: 0 }}>Period peak — details</h2>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            {peak.isForecast === false ? 'settled actual' : 'forecast'}
          </span>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 13, color: 'var(--text-dim)' }}>
            {fmtFullDate(peak.date)} · {hourLabel(peak.hour)} · {series}
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--danger)', marginTop: 2 }}>
            {fmtValue(peak.value)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>
            {peak.temperature != null ? `${Math.round(peak.temperature)}°F outside · ` : ''}
            {peak.lmp != null ? `${fmtLmp(peak.lmp)}/MWh wholesale` : 'no LMP recorded'}
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            7-hour window · 3 before, peak, 3 after
          </div>
          <table className="hist-table">
            <thead>
              <tr>
                <th>Hour</th>
                <th>Day</th>
                <th className="num">{series}</th>
                <th className="num">$/MWh</th>
                <th className="num">°F</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {cells.map((c, i) => {
                const r = c.rec;
                const offTxt = c.offset === 0 ? '— PEAK' : (c.offset > 0 ? `+${c.offset}h` : `${c.offset}h`);
                return (
                  <tr key={i} style={c.isPeak ? { background: 'var(--danger-bg)', fontWeight: 700 } : undefined}>
                    <td>{hourLabel(c.hour)} <span style={{ color: 'var(--text-dim)', fontWeight: 400, fontSize: 11 }}> {offTxt}</span></td>
                    <td>{c.date.slice(5)}</td>
                    <td className="num">{r ? fmtValue(r.value) : '—'}</td>
                    <td className="num">{r ? fmtLmp(r.lmp) : '—'}</td>
                    <td className="num">{r?.temperature != null ? Math.round(r.temperature) : '—'}</td>
                    <td>{r ? (r.isForecast ? 'forecast' : 'actual') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
